"use client";
import * as Yup from "yup";

import {
  InsuredParty,
  type TravelCoveredPerson,
  type TravelEntryPage,
  type TravelFamilyMember,
  type TravelQuote,
} from "@/api_client";
import { getErrorMessage } from "@/utils/errorMessage";

import { AgeValidator } from "../../shared/ageValidators";
import { validSSN } from "../../shared/ssnValidator";

const buildFamilyMemberSchema = (content?: TravelEntryPage | null) =>
  Yup.object<
    Omit<
      TravelFamilyMember,
      "firstName" | "lastName" | "streetAddress" | "municipality"
    >
  >({
    ssn: Yup.string()
      .required(
        getErrorMessage(
          content?.spouseSsnQuestion?.validationMessages?.value,
          "required",
        ),
      )
      .test(
        "valid-ssn",
        getErrorMessage(
          content?.spouseSsnQuestion?.validationMessages?.value,
          "valid-ssn",
        ) ?? "",
        (value) => validSSN(value),
      ),
  });

const buildCoveredPersonSchema = (content?: TravelEntryPage | null) =>
  Yup.object<
    Omit<TravelCoveredPerson, "postalCode" | "permanentResidentOfFinland">
  >({
    insuredParty: Yup.mixed<InsuredParty>()
      .required(
        getErrorMessage(
          content?.coveredPersonQuestion?.validationMessages?.value,
          "required",
        ),
      )
      .oneOf([InsuredParty.POLICY_HOLDER, InsuredParty.FAMILY_MEMBER]),
    familyMember: Yup.object().when("insuredParty", {
      is: "FamilyMember",
      then: () =>
        buildFamilyMemberSchema(content).required(
          getErrorMessage(
            content?.coveredPersonQuestion?.validationMessages?.value,
            "required",
          ),
        ),
      otherwise: () => Yup.object().optional(),
    }),
  });

// Form only deals with a minimal subset of ITravel
export type TravelEntryFormValues = Pick<
  TravelQuote,
  "productType" | "coveredPerson" | "policyHolderSsn"
>;

export const TravelEntryValidationSchema = (
  content?: TravelEntryPage | null,
): Yup.ObjectSchema<TravelEntryFormValues> => {
  const schema: Yup.ObjectSchema<TravelEntryFormValues> = Yup.object({
    coveredPerson: buildCoveredPersonSchema(content).required(),
    policyHolderSsn: Yup.string()
      .required(
        getErrorMessage(
          content?.entryPageCommon?.ssnQuestion?.validationMessages?.value,
          "required",
        ),
      )
      .test(
        "valid-ssn",
        getErrorMessage(
          content?.entryPageCommon?.ssnQuestion?.validationMessages?.value,
          "valid-ssn",
        ) ?? "",
        validSSN,
      )
      .test(
        "is-18+",
        getErrorMessage(
          content?.entryPageCommon?.ssnQuestion?.validationMessages?.value,
          "is-18+",
        ) ?? "",
        (ssn: string) => {
          const ageValidator = new AgeValidator(ssn);
          return ageValidator.isAtLeast18;
        },
      ),
  }).test(
    "coveredPerson.familyMember.ssn",
    getErrorMessage(
      content?.spouseSsnQuestion?.validationMessages?.value,
      "required",
    ) ?? "",
    (value: Partial<TravelQuote>, context) => {
      if (value?.coveredPerson?.familyMember?.ssn === value?.policyHolderSsn) {
        return context.createError({
          path: "coveredPerson.familyMember.ssn",
          message:
            getErrorMessage(
              content?.spouseSsnQuestion?.validationMessages?.value,
              "duplicate-ssn",
            ) ?? "",
        });
      }
      return true;
    },
  ) as Yup.ObjectSchema<TravelEntryFormValues>;
  return schema;
};
